import React, { useEffect } from 'react';
import {
	OverlayModalComponentProps,
	OverlayModalProps,
	PiletApi,
} from '@sharefiledev/sharefile-appshell';
import { App, Button, Flex, Spin } from 'antd';
import antdTheme from 'antd/es/theme';
import { openDesktopApplication } from '../../Addins/addin';
import { useAppStore } from '../../common/store';
import { EditableTemplateCreateButtonAnalyticId } from '../../common/tracking';
import {
	DocGenExtensionSlots,
	EditDesktopModalExtensionSlotName,
	localStorageSignedInOnMSDesktopApp,
	SignIntoShareFileModalExtensionSlotName,
	WorkflowType,
} from '../../constants';
import { DocgenClient } from '../../data/docgenClient';
import { DocumentTemplate } from '../../data/interfaces/DocumentTemplate';
import { CopyFileFromSharefilePayload } from '../../data/interfaces/DocumentTemplateFile';
import { ProgressState } from '../../pages/Start/SelectDocument/types';
import { UploadPreview } from '../../pages/Start/UploadPreview';
import { t } from '../../utils';
import { FileUploadComponent } from '../FileUploadComponent';

export type UploadDocxFileModalProps = {
	app: PiletApi;
	cancelInteraction: OverlayModalComponentProps<
		typeof DocGenExtensionSlots.UPLOAD_DOCX_FILE_MODAL
	>['cancelInteraction'];

	completeInteraction: OverlayModalComponentProps<
		typeof DocGenExtensionSlots.UPLOAD_DOCX_FILE_MODAL
	>['completeInteraction'];
};

export const UploadDocxFileModal = ({
	app,
	cancelInteraction,
	completeInteraction,
}: UploadDocxFileModalProps) => {
	const [uploadState, setUploadState] = React.useState<ProgressState>('');
	const [isRemoveFileDisabled, setIsRemoveFileDisabled] = React.useState(false);

	const { notification } = App.useApp();

	const {
		resetUrlSliceData,
		templateMetadata,
		updateDocumentTemplate,
		documentTemplate,
		fetchThumbnailUrl,
		updateTemplateMetadata,
		setUploadDocxFileModalCloseHandlers,
	} = useAppStore(state => ({
		resetUrlSliceData: state.resetUrlSliceData,
		templateMetadata: state.templateMetadata,
		updateDocumentTemplate: state.updateDocumentTemplate,
		documentTemplate: state.documentTemplate,
		fetchThumbnailUrl: state.fetchThumbnailUrl,
		updateTemplateMetadata: state.updateTemplateMetadata,
		setUploadDocxFileModalCloseHandlers: state.setUploadDocxFileModalCloseHandlers,
	}));

	useEffect(() => {
		if (cancelInteraction && completeInteraction) {
			setUploadDocxFileModalCloseHandlers({
				cancelInteraction,
				completeInteraction,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const onMount = async () => {
			if (
				templateMetadata?.workflow === WorkflowType.ContentViewer ||
				templateMetadata?.workflow === WorkflowType.ContextMenu
			) {
				try {
					setUploadState('started');
					const payload: CopyFileFromSharefilePayload = {
						fileName: templateMetadata?.selectedItem?.FileName,
						fileId: templateMetadata?.selectedItem?.Id,
						overwrite: true,
					};
					const response = await DocgenClient.copyFileFromSharefile(
						documentTemplate?.rid,
						payload
					);

					if (response !== null) {
						setUploadState('storing');
						updateDocumentTemplate({
							file: {
								...documentTemplate?.file,
								fileName: templateMetadata?.selectedItem?.FileName,
							},
						});
						try {
							await fetchThumbnailUrl(documentTemplate?.rid);
						} catch (error: unknown) {
							notification.error({
								message: t('genericError'),
							});
						}
						setUploadState('ready');
					}
				} catch (_) {
					notification.error({
						message: t('genericError'),
					});
				}
			}
		};
		if (documentTemplate?.rid) {
			onMount();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documentTemplate?.rid]);

	function resetUploadState() {
		setUploadState('');
		resetUrlSliceData();
		updateTemplateMetadata(null);
		updateDocumentTemplate({ file: null });
	}

	const resetUpload = () => {
		resetUploadState();
		try {
			deleteUploadedFile(documentTemplate?.rid);
		} catch {
			notification.error({
				message: t('genericError'),
			});
		}
	};

	return (
		<>
			{!documentTemplate?.rid ? (
				<Spin size="large" data-testid="upload-docx-file-modal-spinner" />
			) : uploadState ? (
				<Flex justify="center">
					<UploadPreview
						uploadState={uploadState}
						resetUpload={resetUpload}
						isRemoveFileDisabled={isRemoveFileDisabled}
						setIsRemoveFileDisabled={setIsRemoveFileDisabled}
					/>
				</Flex>
			) : (
				<FileUploadComponent
					setUploadState={setUploadState}
					resetUploadState={resetUploadState}
				/>
			)}
			<UploaderDocxFileModalFooter
				app={app}
				onCancel={cancelInteraction}
				documentTemplate={documentTemplate}
				onSuccess={completeInteraction}
			/>
		</>
	);
};

const deleteUploadedFile = async (rid: DocumentTemplate['rid']) => {
	await DocgenClient.deleteUploadedFile(rid);
};

export const onUploadDocxFileModalCancel = async () => {
	const { resetStore, documentTemplate } = useAppStore.getState();
	if (documentTemplate?.rid) {
		DocgenClient.deleteTemplate(documentTemplate?.rid);
		resetStore();
	}
};

export const getUploadDocxFileModalProps = (
	overrideOverlayModalProps?: OverlayModalProps
) => {
	const overlayModalProps = {
		title: t('uploadDocxFileModal.title'),
		onCancel: onUploadDocxFileModalCancel,
		centered: true,
		width: 840,
		footer: null,
		styles: {
			body: {
				marginTop: antdTheme.getDesignToken().marginMD,
			},
		},
		...overrideOverlayModalProps,
	};

	return overlayModalProps;
};

type UploaderDocxFileModalFooterProps = {
	app: PiletApi;
	onCancel: () => void;
	documentTemplate: DocumentTemplate;
	onSuccess: () => void;
};

const UploaderDocxFileModalFooter = ({
	app,
	onCancel,
	documentTemplate,
	onSuccess,
}: UploaderDocxFileModalFooterProps) => {
	return (
		<Flex>
			{documentTemplate?.rid && <CreateButton app={app} onSuccess={onSuccess} />}
			<Button
				onClick={() => {
					onCancel();
				}}
			>
				{t('cancel')}
			</Button>
		</Flex>
	);
};

type CreateButtonProps = {
	app: PiletApi;
	onSuccess: () => void;
};

const CreateButton = ({ app, onSuccess }: CreateButtonProps) => {
	const { notification } = App.useApp();
	const { documentTemplate, isLoading, setIsLoading, user } = useAppStore(state => ({
		documentTemplate: state.documentTemplate,
		isLoading: state.isLoading,
		setIsLoading: state.setIsLoading,
		user: state.user,
	}));

	const openEditDesktopModal = () => {
		app.showModal(EditDesktopModalExtensionSlotName);
	};

	const onClick = async () => {
		try {
			setIsLoading(true);
			const localStorageKey = user.Id + localStorageSignedInOnMSDesktopApp;
			if (localStorage.getItem(localStorageKey) !== 'true') {
				app.showModal(SignIntoShareFileModalExtensionSlotName);
			} else {
				await openDesktopApplication();
				onSuccess();
				setTimeout(() => {
					openEditDesktopModal();
				}, 2000);
			}
		} catch {
			notification.error({
				message: t('genericError'),
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Button
			type="primary"
			onClick={onClick}
			disabled={!documentTemplate?.file?.fileName}
			loading={isLoading}
			data-analytics-id={EditableTemplateCreateButtonAnalyticId}
		>
			{t('uploadDocxFileModal.okButtonText')}
		</Button>
	);
};

export default UploadDocxFileModal;
